import React, { useState, useContext, useRef } from 'react';
import { LanguageContext } from '../../context/LanguageContext';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import { FaTimesCircle, FaPrint, FaSpinner } from 'react-icons/fa';
import ReceiptPrinter from './payment/ReceiptPrinter';
import { prepareOrderForPrinting, loadPrinterSettings, getDefaultSettings } from './return-order/PrinterService';

const PrintReceiptModal = ({ isOpen, onClose }) => {
    const receiptPrinterRef = useRef();
    const { language } = useContext(LanguageContext);
    const [orderId, setOrderId] = useState('');
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async () => {
        if (!orderId.trim()) return;
        
        setLoading(true);
        setError(null);
        try {
            // Pass a query parameter to indicate we want to search in both tables
            const response = await axios.get(`orders/${orderId}`, {
                params: {
                    search_all: true // This parameter can tell the backend to search in both tables
                }
            });
            
            if (!response.data || !response.data.data) {
                throw new Error('No data received');
            }

            const orderData = response.data.data;
            
            // Determine payment method based on order data or id format
            let paymentMethod = orderData.payment_method;
            
            // If payment_method isn't explicitly set, try to determine it
            if (!paymentMethod) {
                // Check if this ID follows a pattern for card orders
                const orderIdString = String(orderData.id || orderData.order_number || '');
                if (orderIdString.includes('CARD') || orderIdString.includes('card')) {
                    paymentMethod = 'card';
                } else {
                    // Default to cash if we can't determine
                    paymentMethod = 'cash';
                }
            }
            
            // Determine if this is a card order based on the data source or API response
            const isCardOrder = paymentMethod === 'card' || 
                              (orderData.order_number && orderData.order_number.includes('CARD')) ||
                              orderData.resource_type === 'SalesOrderCardResource';
            
            const processedOrder = {
                id: orderData.id,
                order_number: orderData.order_number,
                payment_method: paymentMethod,
                customer_type: orderData.customer_type || 'customer',
                company_name: orderData.company_name || '',
                company_tax_number: orderData.company_tax_number || '',
                status: orderData.status, // Include status for display purposes
                items: orderData.items.map(item => {
                    // Preserve the original item data exactly as received
                    return {
                        name: language === 'ar' ? item.name_ar : item.name,
                        name_ar: item.name_ar || '',
                        quantity: parseFloat(item.quantity) || 1,
                        unit_price: parseFloat(item.unit_price) || 0,
                        price: parseFloat(item.price || item.unit_price * item.quantity) || 0
                    };
                }),
                total: orderData.total,
                created_at: orderData.created_at,
                subtotal: orderData.subtotal || 0,
                tax: orderData.tax || 0,
                delivery_fee: orderData.delivery_fee || 0,
                qr_code: orderData.qr_code,
                // Add flag to identify card orders for processing
                isCardOrder: isCardOrder
            };
            setOrder(processedOrder);
        } catch (err) {
            console.error('Search Error:', err);
            setError(language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found');
            setOrder(null);
        } finally {
            setLoading(false);
        }
    };
    
    const handlePrintReceipt = async (isReturnReceipt = false) => {
        if (!order) return;

        try {
            // Check if the printer reference is available
            if (!receiptPrinterRef || !receiptPrinterRef.current) {
                console.error('Receipt printer reference is not available');
                toast.error(language === 'ar' ? 'مرجع الطابعة غير متوفر' : 'Printer reference not available');
                return;
            }
            
            if (receiptPrinterRef.current.connectQZ) {
                try {
                    await receiptPrinterRef.current.connectQZ();
                } catch (connectError) {
                    console.error('Failed to connect to QZ-Tray:', connectError);
                    toast.error(language === 'ar' 
                        ? 'فشل الاتصال بـ QZ-Tray. هل هو قيد التشغيل؟' 
                        : 'Failed to connect to QZ-Tray. Is it running?');
                }
            }
            
            // Prepare order data for printing
            const orderForPrinting = {
                ...order,
                isReturn: isReturnReceipt,
                // Explicitly carry forward the isCardOrder flag
                isCardOrder: order.isCardOrder || false
            };
            
            const enhancedOrder = prepareOrderForPrinting(orderForPrinting);
            // Explicitly set isReturn flag after preparation
            enhancedOrder.isReturn = isReturnReceipt;
            
            // Ensure card order flag is preserved
            if (orderForPrinting.isCardOrder) {
                enhancedOrder.isCardOrder = true;
            }
            
            // Get printer settings
            // eslint-disable-next-line no-unused-vars
            const settings = await loadPrinterSettings(language);
            
            // Print the receipt
            if (receiptPrinterRef.current) {
                try {
                    // Log QR code preview for debugging
                    if (enhancedOrder.qr_code) {
                        console.log('QR Code preview:', enhancedOrder.qr_code.substring(0, 50) + '...');
                    }
                    
                    await receiptPrinterRef.current.printReceipt(enhancedOrder);
                    toast.success(language === 'ar' ? 'جاري طباعة الإيصال' : 'Printing receipt');
                } catch (error) {
                    console.error('Error calling printReceipt:', error);
                    toast.error(language === 'ar' ? 'حدث خطأ أثناء طباعة الإيصال' : 'Error printing receipt');
                }
            }
            
        } catch (err) {
            console.error('Print Error:', err);
            toast.error(
                language === 'ar'
                    ? 'حدث خطأ أثناء طباعة الإيصال'
                    : 'Error printing receipt'
            );
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        if (language === 'ar') {
            const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
            return `${toArabicNumeral(day)}/${toArabicNumeral(month)}/${toArabicNumeral(year)}`;
        }
        return `${day}/${month}/${year}`;
    };

    return (
        <div className={`modal ${isOpen ? 'show' : ''}`} onClick={onClose}>
            {/* Hidden ReceiptPrinter component with ref */}
            <div style={{ display: 'none' }}>
                <ReceiptPrinter 
                    ref={receiptPrinterRef} 
                    settings={getDefaultSettings()} 
                />
            </div>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium">
                            {language === 'ar' ? 'طباعة إيصال' : 'Print Receipt'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <FaTimesCircle className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Search Form */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {language === 'ar' ? 'رقم الطلب' : 'Order ID'}
                            </label>
                            <div className="flex space-x-2 rtl:space-x-reverse">
                                <input
                                    type="text"
                                    value={orderId}
                                    onChange={(e) => setOrderId(e.target.value)}
                                    placeholder={language === 'ar' ? 'أدخل رقم الطلب' : 'Enter order ID'}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button
                                    onClick={handleSearch}
                                    disabled={loading || !orderId.trim()}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <FaSpinner className="animate-spin h-4 w-4" />
                                    ) : (
                                        language === 'ar' ? 'بحث' : 'Search'
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}

                        {/* Order Details */}
                        {order && (
                            <div className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">
                                        {language === 'ar' ? 'رقم الطلب:' : 'Order ID:'}
                                    </span>
                                    <span className="font-medium">{order.order_number || order.id}</span>
                                </div>
                                
                                {order.status && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-500">
                                            {language === 'ar' ? 'الحالة:' : 'Status:'}
                                        </span>
                                        <span className={`font-medium px-2 py-1 rounded text-xs ${
                                            order.status === 'returned' 
                                                ? 'bg-yellow-100 text-yellow-800' 
                                                : 'bg-green-100 text-green-800'
                                        }`}>
                                            {order.status === 'returned' 
                                                ? (language === 'ar' ? 'مُرجع' : 'Returned')
                                                : (language === 'ar' ? 'مكتمل' : 'Completed')
                                            }
                                        </span>
                                    </div>
                                )}
                                
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500">
                                        {language === 'ar' ? 'المجموع:' : 'Total:'}
                                    </span>
                                    <span className="font-medium">{order.total.toFixed(2)}</span>
                                </div>
                                
                                {order.created_at && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-500">
                                            {language === 'ar' ? 'التاريخ:' : 'Date:'}
                                        </span>
                                        <span className="font-medium">
                                            {formatDate(order.created_at)}
                                        </span>
                                    </div>
                                )}

                                {/* Order Items */}
                                <div className="mt-3 border-t pt-3">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                                        {language === 'ar' ? 'العناصر:' : 'Items:'}
                                    </h4>
                                    <div className="space-y-2">
                                        {order.items.map((item, index) => (
                                            <div key={index} className="flex justify-between text-sm">
                                                <span>
                                                    {item.quantity} x {item.name}
                                                </span>
                                                <span>{item.price.toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Print Buttons */}
                                <div className="flex space-x-2 rtl:space-x-reverse mt-4">
                                    <button
                                        onClick={() => handlePrintReceipt(false)}
                                        disabled={loading}
                                        className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        <FaPrint className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                                        {language === 'ar' ? 'طباعة الإيصال' : 'Print Receipt'}
                                    </button>
                                    
                                    {order.status === 'returned' && (
                                        <button
                                            onClick={() => handlePrintReceipt(true)}
                                            disabled={loading}
                                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                                        >
                                            <FaPrint className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                                            {language === 'ar' ? 'طباعة إيصال إرجاع' : 'Print Return Receipt'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintReceiptModal; 